import { useEffect, useRef, useState } from 'react';
import { addCustomerHealthParent, fetchTrackableParents, TrackableParent } from '../../../services/customerHealthService';

const PICKER_LIMIT = 10;

/**
 * Self-contained "Add Ultimate Parent" modal (portfolio page). Searches the real trackable-parent
 * list server-side (debounced, excluding already-tracked names), inserts the chosen parent + a
 * user_assignment, then calls `onAdded(name)` so the page can navigate to its new detail page.
 */
export function AddParentModal({ excludeParents, currentUserId, onClose, onAdded }: {
  excludeParents: string[];
  currentUserId: string | undefined;
  onClose: () => void;
  onAdded: (name: string) => void;
}) {
  const [q, setQ] = useState('');
  const [options, setOptions] = useState<TrackableParent[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [adding, setAdding] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const reqId = useRef(0);
  const query = q.trim();

  useEffect(() => {
    setError(null);
    const id = ++reqId.current;
    setLoading(true);
    const handle = setTimeout(() => {
      fetchTrackableParents(query, excludeParents, PICKER_LIMIT)
        .then((parents) => { if (reqId.current === id) setOptions(parents); })
        .catch(() => { if (reqId.current === id) setOptions([]); })
        .finally(() => { if (reqId.current === id) setLoading(false); });
    }, 300);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query]);

  const add = async (name: string) => {
    if (adding) return;
    if (!currentUserId) { setError('Could not identify the current user — please sign in again.'); return; }
    setError(null);
    setAdding(name);
    try {
      await addCustomerHealthParent(name, currentUserId);
      onAdded(name);
    } catch (err: any) {
      setError(err?.response?.errors?.[0]?.message || 'Failed to add this ultimate parent. You may not have permission.');
      setAdding(null);
    }
  };

  const list = options || [];
  return (
    <div className="ch-overlay" onClick={onClose}>
      <div className="ch-modal ch-pop" style={{ width: 520 }} onClick={(ev) => ev.stopPropagation()}>
        <div style={{ fontSize: 11, fontWeight: 600, color: '#999', textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 4 }}>Portfolio</div>
        <h3 style={{ margin: '0 0 18px', fontSize: 19, fontWeight: 700, color: '#1a1a1a' }}>Add Ultimate Parent</h3>
        <input value={q} placeholder="Search ultimate parents…" autoFocus onChange={(ev) => setQ(ev.target.value)}
          style={{ width: '100%', border: '1px solid #e0e0e0', borderRadius: 8, padding: '10px 12px', fontFamily: 'inherit', fontSize: 14, color: '#333', outline: 'none', marginBottom: 12 }} />
        {error && <div style={{ fontSize: 13, color: '#b01400', background: 'rgba(225,25,0,.08)', borderRadius: 8, padding: '10px 12px', marginBottom: 12 }}>{error}</div>}
        {options === null ? (
          <div style={{ fontSize: 13, color: '#999', padding: '12px 2px' }}>{loading ? 'Loading ultimate parents…' : 'Could not load ultimate parents.'}</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: '46vh', overflow: 'auto', opacity: loading ? 0.6 : 1 }}>
            {list.length ? list.map((p) => (
              <button key={p.ultimate_parent} onClick={() => add(p.ultimate_parent)} disabled={!!adding} className="ch-parent-pick"
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '12px 14px', borderRadius: 10, border: '1px solid #e0e0e0', background: '#fff', cursor: adding ? 'default' : 'pointer', fontFamily: 'inherit', textAlign: 'left', opacity: adding && adding !== p.ultimate_parent ? 0.5 : 1 }}>
                <div>
                  <div style={{ fontSize: 15, fontWeight: 700, color: '#1a1a1a' }}>{p.ultimate_parent}</div>
                  <div style={{ fontSize: 12, color: '#999', marginTop: 2 }}>{p.stores.toLocaleString() + ' Stores · ' + p.brands + (p.brands === 1 ? ' Brand' : ' Brands')}</div>
                </div>
                <span style={{ fontSize: 13, fontWeight: 600, color: '#4a90e2' }}>{adding === p.ultimate_parent ? 'Adding…' : 'Add'}</span>
              </button>
            )) : <div style={{ fontSize: 13, color: '#999', padding: '12px 2px' }}>{query ? `No ultimate parents match “${query}”.` : 'All ultimate parents are already on your dashboard.'}</div>}
            {list.length === PICKER_LIMIT && (
              <div style={{ fontSize: 12, color: '#999', padding: '2px 2px 0' }}>Showing top {PICKER_LIMIT} matches — refine your search for more.</div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
